import Lenis from 'lenis';

type ScrollListener = () => void;

let lenis: Lenis | null = null;
let rafId = 0;
const listeners = new Set<ScrollListener>();

function emitScroll() {
  listeners.forEach((listener) => listener());
}

/** Start Lenis + shared rAF loop (scroll-linked UI hooks into emitScroll). */
export function initSmoothScroll(): () => void {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reducedMotion) {
    return () => undefined;
  }

  lenis = new Lenis({
    lerp: 0.085,
    smoothWheel: true,
    wheelMultiplier: 0.92,
    touchMultiplier: 1.05,
    autoRaf: false,
  });

  lenis.on('scroll', emitScroll);

  const raf = (time: number) => {
    lenis?.raf(time);
    rafId = requestAnimationFrame(raf);
  };

  rafId = requestAnimationFrame(raf);
  emitScroll();

  return () => {
    cancelAnimationFrame(rafId);
    lenis?.destroy();
    lenis = null;
    listeners.clear();
  };
}

export function subscribeScroll(listener: ScrollListener): () => void {
  listeners.add(listener);
  listener();

  const onNativeScroll = () => listener();
  if (!lenis) {
    window.addEventListener('scroll', onNativeScroll, { passive: true });
  }

  return () => {
    listeners.delete(listener);
    window.removeEventListener('scroll', onNativeScroll);
  };
}

export function scrollToTop(options?: { immediate?: boolean }) {
  const immediate = options?.immediate ?? true;
  if (lenis) {
    lenis.scrollTo(0, { immediate, force: true });
    return;
  }
  window.scrollTo({ top: 0, left: 0, behavior: immediate ? 'auto' : 'smooth' });
}

export function getLenis(): Lenis | null {
  return lenis;
}
